<template>
  <div class="ImportForm">
    <Steps :hasListedSteps="true">
      <template #0>
        <section :title="$t('itemsList')">
          <Field
            class="fullHeight"
            :label="$t('inputExcel')"
            type="textarea"
            :value.sync="importData.input" />
        </section>
      </template>
      <template #0-next>
        <Btn type="submit" @click.native="parse($event)">
          {{ $t('continue') }}
        </Btn>
      </template>
      <template #1>
        <section :title="$t('projectDetails')">
          <Row>
            <Column :lg="6">
              <DatePicker
                :label="$t('date')"
                required
                :value.sync="importData.date" />
            </Column>
            <Column :lg="6">
              <Row>
                <Column :xs="8">
                  <Field
                    :label="$t('projectId')"
                    required
                    :value.sync="importData.orderNo"
                    @focusout="getContactByOrderNo" />
                </Column>
                <Column :xs="4">
                  <Btn
                    class="fetchScoro"
                    :disabled="!importData.orderNo"
                    :isLoading="isFetchingClient"
                    @click="getContactByOrderNo">
                    {{ $t('fetchScoro') }}
                  </Btn>
                </Column>
              </Row>
            </Column>
          </Row>
          <Row>
            <Column :lg="6">
              <Field
                :label="$t('managerId')"
                :options="managerOptions"
                required
                type="select"
                :value.sync="importData.managerId" />
            </Column>
            <Column :lg="6">
              <Field
                disabled
                :label="$t('clientId')"
                required
                :value.sync="importData.clientId" />
            </Column>
          </Row>
          <Field
            disabled
            :label="$t('clientName')"
            required
            :value.sync="importData.clientName" />
          <Field
            disabled
            :label="$t('address')"
            required
            type="textarea"
            :value="address" />
        </section>
      </template>
      <template #2>
        <section class="filledHeight" :title="$t('sendToDirecto')">
          <h4 id="preview" class="label">
            {{ $t('preview') }}
          </h4>
          <Tabs>
            <Sect slot="JSON">
              <Field
                class="fullHeight"
                disabled
                type="textarea"
                :value.sync="importData.preview" />
            </Sect>
            <Sect slot="XML">
              <Field
                class="fullHeight"
                disabled
                type="textarea"
                :value.sync="jsonToXML" />
            </Sect>
          </Tabs>
          <Field
            :label="$t('checkToVerify')"
            type="checkbox"
            :value.sync="isVerified" />
        </section>
      </template>
      <template #2-next>
        <Btn
          class="sendToDirecto"
          :disabled="!isVerified"
          :isLoading="isSendingToDirecto"
          type="submit"
          @click="sendToDirecto">
          {{ $t('sendToDirecto') }}
        </Btn>
      </template>
    </Steps>
  </div>
</template>

<script>
import { actions, getters } from '../../utils'
import DatePicker from 'components/DatePicker'
import Steps from 'components/Steps'
import Tabs from 'components/Tabs'
import builder from 'xmlbuilder'
import { scrollToHash } from '../../../router/smoothScroll'

export default {
  components: {
    DatePicker,
    Tabs,
    Steps,
  },
  props: {
    importData: Object,
  },
  data() {
    return {
      isVerified: false,
      isFetchingClient: false,
      isSendingToDirecto: false,
    }
  },
  computed: {
    address() {
      const { address1, address2, address3 } = this.importData
      return [address1, address2, address3]
        .filter(Boolean)
        .join(', ')
    },
    jsonToXML() {
      const {
        address1,
        address2,
        address3,
        date,
        preview,
        clientName,
        clientId,
        managerId,
        orderNo,
      } = this.importData

      const customOrderNo = orderNo
        ? `${orderNo}_${this.$d(new Date(), 'shortest').replace(/-/g, '')}`
        : null

      const xml = builder.create('orders')
        .ele('order', {
          appkey: process.env.DIRECTO_API_KEY,
          date: date?.toISOString().slice(0, 10),
          cust_order_no: customOrderNo,
          customer: clientId,
          customer_name: clientName,
          del_address1: address1,
          del_address2: address2,
          del_address3: address3,
          stock: 'PAGRINDINIS',
          salesman: managerId,
        })

      if (!preview) return

      JSON.parse(preview).forEach((row, index) => {
        const { rn, rv, code, quantity } = row
        xml.ele('line', {
          rn,
          rv,
          pricewovat: 0,
          quantity,
          code,
        })
      })

      return xml.end({ pretty: true })
    },
    managerOptions() {
      return [
        'AGNIUS',
        'ANDRZEJ',
        'DARIA',
        'DZIUGAS',
        'EIMANTAS',
        'EVELINA',
        'GEDIMINAS',
        'GERDA',
        'JOLANTA',
        'JULIJA',
        'LEINONEN',
        'LIUTAURAS',
        'MANTAS',
        'MARIJA',
      ]
    },
    ...getters(),
  },
  methods: {
    parse(event) {
      const { input } = this.importData

      try {
        if (!input) throw new Error(this.$t('inputCannotBeEmpty'))

        const result = input
          .trim('\n')
          .split('\n')
          .map(row => {
            const columns = row
              .replace(/(\t)\1+/g, '\t')
              .split('\t')

            const [
              rn,
              rv,
              code,
              label,
              quantity,
            ] = columns

            if (!code || label === 'Produkto pavadinimas, kodas' || columns.length < 5) return

            return {
              rn,
              rv,
              code,
              label,
              quantity: parseFloat(quantity),
            }
          })
          .filter(Boolean)
          .filter(row => row.code)

        if (result.length === 0) throw new Error(this.$t('badInputFormat'))

        this.importData.preview = JSON.stringify(result, null, '  ')
      } catch (error) {
        event.preventDefault()
        this.ERROR(error)
      }
    },
    clearClientData() {
      this.importData.clientName = null
      this.importData.clientId = null
      this.importData.address1 = null
      this.importData.address2 = null
      this.importData.address3 = null
    },
    async getContactByOrderNo() {
      const { orderNo } = this.importData

      this.clearClientData()

      try {
        if (!orderNo) {
          throw new Error('Project ID cannot not be empty')
        }

        this.isFetchingClient = true
        const [order] = await this.FIND_SCORO_ORDER({ no: orderNo })

        const contactId = order.company_id

        if (!contactId) {
          throw new Error('Project has empty company_id')
        }

        const contact = await this.GET_SCORO_CONTACT(contactId)

        if (contact) {
          this.importData.clientName = contact.search_name
          this.importData.clientId = contact.contact_id
          const [address] = contact.addresses

          if (address) {
            this.importData.address1 = address.street
            this.importData.address2 = address.city
            this.importData.address3 = address.zipcode
          }
        }

        scrollToHash('#preview')
      } catch (error) {
        if (!error.ignore) {
          this.ERROR(error)
        }
      } finally {
        this.isFetchingClient = false
      }
    },
    async sendToDirecto() {
      this.isSendingToDirecto = true

      try {
        await this.CREATE_DIRECTO({ xmlData: this.jsonToXML })
        this.SUCCESS('Sėkmingas siuntimas')
      } catch (error) {
        if (!error.ignore) {
          this.ERROR(error)
        }
      } finally {
        this.isSendingToDirecto = false
      }
    },
    ...actions(
      'CREATE_DIRECTO',
      'FIND_SCORO_ORDER',
      'GET_SCORO_CONTACT',
    ),
  },
}
</script>

<style lang="scss">
.ImportForm {
  height: 100%;

  .sendToDirecto {
    text-transform: uppercase;
  }

  .Btn.fetchScoro {
    margin-top: 2.5rem;
  }

  .Steps {
    display: flex;
    flex-direction: column;
    height: 100%;

    > form {
      height: 100%;

      > span {
        display: flex;
        flex-direction: column;
        height: 100%;

        > .current {
          height: 100%;
        }

        section {
          height: 100%;

          .fullHeight {
            display: flex;
            flex-direction: column;
            height: 100%;

            > .Field {
              height: 100%;

              textarea {
                height: 100%;
              }
            }
          }
        }
      }
    }
  }

  h4.label {
    line-height: 2.5rem;
  }

  .filledHeight {
    display: flex;
    flex-direction: column;

    .Tabs {
      display: flex;
      flex-direction: column;
      height: 100%;

      .body {
        height: 100%;

        .Sect {
          height: 100%;
        }
      }
    }

    > .fullHeight {
      display: flex;
      flex-direction: column;
      height: 100%;

      .Field {
        height: 100%;

        textarea {
          height: 100%;
          white-space: pre;
        }
      }
    }
  }
}
</style>

<i18n>
{
  "en": {
    "inputExcel": "Input table from Excel",
    "itemsList": "Items list",
    "projectDetails": "Project details",
    "sendToDirecto": "Send to Directo",
    "preview": "Preview",
    "date": "Date",
    "projectId": "Project ID (Scoro Order ID)",
    "managerId": "Manager ID",
    "address": "Address",
    "clientId": "Client ID",
    "clientName": "Client Name",
    "address": "Address",
    "fetchScoro": "Fetch Scoro",
    "checkToVerify": "Check to verify output",
    "parse": "Parse",
    "badInputFormat": "Bad input format.",
    "continue": "Continue",
    "inputCannotBeEmpty": "Input cannot be empty"
  }
}
</i18n>
